import type { AssessmentTemplateDeleteResult } from '../../model/assessments/types'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssessmentTemplateDeleteApiOptions {
  templateId: string | number
  onStart?: () => void
  onSuccess?: () => void
  /** The `409` guard fired: `assessmentsCount` attempts reference the template. Distinct from
   *  `onError` — the caller offers *duplicate* rather than reporting a failure. */
  onBlocked?: (assessmentsCount: number, message: string) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

const HTTP_CONFLICT = 409

/** Reads the guard body off a thrown axios error: `409` + `data.assessments_count` (§5).
 *  Returns `null` for anything that is not the guard. */
const getBlockedResult = (err: unknown): Extract<AssessmentTemplateDeleteResult, { status: 'blocked' }> | null => {
  const response = (err as { response?: { status?: number, data?: ApiResponse } })?.response
  if (response?.status !== HTTP_CONFLICT) return null

  const count = Number(response?.data?.data?.assessments_count)
  if (!Number.isFinite(count)) return null

  return {
    status: 'blocked',
    assessmentsCount: count,
    message: response?.data?.message || 'Assessment template cannot be deleted while assessments exist.',
  }
}

/**
 * `DELETE /admin-api/assessment-templates/{template_id}` — destroy (contract §5).
 *
 * Returns a discriminated result rather than a boolean: the `409` guard carries the number of
 * attempts blocking the delete, which the modal needs in order to offer *duplicate*. That case
 * raises no error toast — the caller owns the message.
 */
export const useAssessmentTemplateDeleteApi = async (
  options: AssessmentTemplateDeleteApiOptions,
): Promise<AssessmentTemplateDeleteResult> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assessment-templates.destroy', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return { status: 'failed', message: msg }
    }

    const { data } = await $axios.delete<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return { status: 'failed', message: data.message }
    }

    toast.success('Assessment template deleted')
    options.onSuccess?.()
    return { status: 'deleted' }
  } catch (err: unknown) {
    const blocked = getBlockedResult(err)

    if (blocked) {
      options.onBlocked?.(blocked.assessmentsCount, blocked.message)
      return blocked
    }

    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return { status: 'failed', message: msg }
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
